// src/services/firebase-auth.service.ts
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithRedirect,
} from "firebase/auth";
import { auth, googleProvider } from "../config/firebase";
import { supabase } from "./supabase";
import { Database } from "../types/supabase.types";

export interface User {
  id: string;
  email: string;
  name: string;
  avatar_url: string | null;
  provider: string;
  created_at: string;
  updated_at: string;
  last_sign_in: string;
  is_active: boolean;
}

export interface AuthError {
  message: string;
  code?: string;
}

type UserRow = Database["public"]["Tables"]["users"]["Row"];

class FirebaseAuthService {
  // SIGN IN WITH GOOGLE - FIXED VERSION
  async signInWithGoogle(): Promise<{
    user: User | null;
    error: AuthError | null;
  }> {
    try {
      // Set custom parameters to avoid popup issues
      googleProvider.setCustomParameters({
        prompt: "select_account",
      });

      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;

      if (!firebaseUser.email) {
        throw new Error("No email provided");
      }

      // DON'T use supabase.auth.signInWithIdToken - that's causing 400 error
      // Instead, directly manage users in our users table

      // Try to get existing user from our users table
      const { data: existingUser, error: fetchError } = await supabase
        .from("users")
        .select("*")
        .eq("firebase_uid", firebaseUser.uid)
        .maybeSingle();

      if (fetchError && fetchError.code !== "PGRST116") {
        console.error("Error fetching user:", fetchError);
        throw fetchError;
      }

      let userData: UserRow | null = null;

      if (!existingUser) {
        // Create new user in Supabase users table
        const { data: newUser, error: createError } = await supabase
          .from("users")
          .insert({
            firebase_uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName || firebaseUser.email.split("@")[0],
            avatar_url: firebaseUser.photoURL,
            provider: "google",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            last_sign_in: new Date().toISOString(),
            is_active: true,
          })
          .select()
          .single();

        if (createError) {
          console.error("Create error:", createError);
          throw createError;
        }
        userData = newUser;
      } else {
        // Update existing user
        const { data: updatedUser, error: updateError } = await supabase
          .from("users")
          .update({
            name: firebaseUser.displayName || existingUser.name,
            avatar_url: firebaseUser.photoURL || existingUser.avatar_url,
            updated_at: new Date().toISOString(),
            last_sign_in: new Date().toISOString(),
          })
          .eq("firebase_uid", firebaseUser.uid)
          .select()
          .single();

        if (updateError) {
          console.error("Update error:", updateError);
          throw updateError;
        }
        userData = updatedUser;
      }

      return {
        user: userData ? this.mapUser(userData) : null,
        error: null,
      };
    } catch (error: any) {
      console.error("Error signing in with Google:", error);

      // Handle specific Firebase errors
      if (error.code === "auth/popup-blocked") {
        return {
          user: null,
          error: {
            message: "Pop-up was blocked. Please allow pop-ups for this site.",
            code: error.code,
          },
        };
      }

      if (error.code === "auth/popup-closed-by-user") {
        return {
          user: null,
          error: {
            message: "Sign-in cancelled. Please try again.",
            code: error.code,
          },
        };
      }

      return {
        user: null,
        error: {
          message: error?.message || "Failed to sign in with Google",
          code: error?.code,
        },
      };
    }
  }

  async signInWithGoogleRedirect(): Promise<void> {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: "select_account",
    });
    await signInWithRedirect(auth, provider);
  }

  // SIGN OUT
  async signOut(): Promise<void> {
    try {
      await firebaseSignOut(auth);
      // Clear any local storage
      localStorage.removeItem("supabase.auth.token");
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  }

  // GET CURRENT USER
  async getCurrentUser(): Promise<User | null> {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        unsubscribe();

        if (!firebaseUser?.email) {
          resolve(null);
          return;
        }

        const { data: userData } = await supabase
          .from("users")
          .select("*")
          .eq("firebase_uid", firebaseUser.uid)
          .maybeSingle();

        resolve(userData ? this.mapUser(userData) : null);
      });
    });
  }

  // AUTH STATE CHANGE LISTENER
  onAuthStateChange(callback: (user: User | null) => void): () => void {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser?.email) {
        callback(null);
        return;
      }

      const { data: userData } = await supabase
        .from("users")
        .select("*")
        .eq("firebase_uid", firebaseUser.uid)
        .maybeSingle();

      callback(userData ? this.mapUser(userData) : null);
    });
  }

  private mapUser(userData: UserRow): User {
    return {
      id: userData.id,
      email: userData.email,
      name: userData.name,
      avatar_url: userData.avatar_url,
      provider: userData.provider,
      created_at: userData.created_at || new Date().toISOString(),
      updated_at: userData.updated_at || new Date().toISOString(),
      last_sign_in: userData.last_sign_in || new Date().toISOString(),
      is_active: userData.is_active ?? true,
    };
  }
}

export const firebaseAuthService = new FirebaseAuthService();
export default firebaseAuthService;
